import {
  CASINO_CARD_RANKS,
  CASINO_CARD_SUITS,
  MONEY_WHEEL_PAYS,
  MONEY_WHEEL_SEGMENTS,
  MONEY_WHEEL_SYMBOLS,
  SLOT_REELS,
  SLOT_STOPS,
  type MoneyWheelSymbol,
  type SlotSymbol,
} from "@townsy/contracts";
import { SLOT_MACHINE_NAMES } from "../casinoView";

/**
 * Canvas painting for the casino's textures (ADR-0016): an original card deck, the slot reel strips, the money wheel's
 * face, the blackjack felt, carpets and signs. Pure drawing on a 2D context; the scene puts them on meshes. Canvas
 * y grows downward; a DynamicTexture shows the canvas top at v = 1.
 */
export type Ctx2D = CanvasRenderingContext2D;

const FONT = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';
const SUIT_GLYPH: Record<string, string> = { S: "♠", H: "♥", D: "♦", C: "♣" };

export interface UvRect {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

/** UVs of a canvas pixel rectangle (canvas top = v 1). */
export function uvOf(x: number, y: number, w: number, h: number, texW: number, texH: number): UvRect {
  return { u0: x / texW, u1: (x + w) / texW, v1: 1 - y / texH, v0: 1 - (y + h) / texH };
}

function roundRect(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ---------------------------------------------------------------------------------------------------------------------
// Cards: 13 ranks x 4 suits + a back, in a 1024 x 512 atlas (cells 78 x 102)
// ---------------------------------------------------------------------------------------------------------------------

export const CARD_ATLAS = { width: 1024, height: 512, cellW: 78, cellH: 102 } as const;

/** Atlas UVs of a card ("AS", "TH"; "XX" = the back). */
export function cardUv(card: string): UvRect {
  const A = CARD_ATLAS;
  let col = 0;
  let row = 4;
  if (card !== "XX") {
    col = Math.max(0, CASINO_CARD_RANKS.indexOf(card[0] as (typeof CASINO_CARD_RANKS)[number]));
    row = Math.max(0, CASINO_CARD_SUITS.indexOf(card[1] as (typeof CASINO_CARD_SUITS)[number]));
  }
  // Inset half a pixel so neighbouring cells never bleed in.
  return uvOf(col * A.cellW + 1, row * A.cellH + 1, A.cellW - 2, A.cellH - 2, A.width, A.height);
}

export function paintCards(ctx: Ctx2D): void {
  const A = CARD_ATLAS;
  ctx.clearRect(0, 0, A.width, A.height);
  ctx.fillStyle = "#20242C";
  ctx.fillRect(0, 0, A.width, A.height);
  CASINO_CARD_SUITS.forEach((suit, row) => {
    CASINO_CARD_RANKS.forEach((rank, col) => paintCard(ctx, col * A.cellW, row * A.cellH, A.cellW, A.cellH, rank === "T" ? "10" : rank, suit));
  });
  paintCardBack(ctx, 0, 4 * A.cellH, A.cellW, A.cellH);
}

function paintCard(ctx: Ctx2D, x: number, y: number, w: number, h: number, rank: string, suit: string): void {
  const red = suit === "H" || suit === "D";
  const ink = red ? "#C0262D" : "#1B1F2A";
  roundRect(ctx, x + 2, y + 2, w - 4, h - 4, 7);
  ctx.fillStyle = "#FFFDF8";
  ctx.fill();
  ctx.strokeStyle = "#B9B2A3";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Big rank and suit, readable from across the table.
  ctx.font = `800 ${rank === "10" ? 38 : 46}px ${FONT}`;
  ctx.fillText(rank, x + w / 2, y + h * 0.34);
  ctx.font = `700 40px ${FONT}`;
  ctx.fillText(SUIT_GLYPH[suit] ?? "", x + w / 2, y + h * 0.74);
}

function paintCardBack(ctx: Ctx2D, x: number, y: number, w: number, h: number): void {
  roundRect(ctx, x + 2, y + 2, w - 4, h - 4, 7);
  ctx.fillStyle = "#FFFDF8";
  ctx.fill();
  roundRect(ctx, x + 7, y + 7, w - 14, h - 14, 5);
  ctx.fillStyle = "#2148B8";
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = "#5C82E8";
  ctx.lineWidth = 2;
  for (let k = -h; k < w + h; k += 9) {
    ctx.beginPath();
    ctx.moveTo(x + k, y);
    ctx.lineTo(x + k + h, y + h);
    ctx.moveTo(x + k + h, y);
    ctx.lineTo(x + k, y + h);
    ctx.stroke();
  }
  ctx.restore();
  ctx.fillStyle = "#F2C94C";
  ctx.font = `800 22px ${FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("T", x + w / 2, y + h / 2);
}

// ---------------------------------------------------------------------------------------------------------------------
// Slot reels: the three strips side by side, 20 cells each (256 x 1280; cells 85 x 64)
// ---------------------------------------------------------------------------------------------------------------------

export const REEL_ATLAS = { width: 256, height: 1280, cellW: 85, cellH: 64 } as const;

export function paintReels(ctx: Ctx2D): void {
  const A = REEL_ATLAS;
  ctx.fillStyle = "#FFFDF6";
  ctx.fillRect(0, 0, A.width, A.height);
  SLOT_REELS.forEach((reel, r) => {
    reel.forEach((symbol, i) => {
      const x = r * A.cellW;
      const y = i * A.cellH;
      const grad = ctx.createLinearGradient(x, y, x, y + A.cellH);
      grad.addColorStop(0, "#F1EBDD");
      grad.addColorStop(0.5, "#FFFFFF");
      grad.addColorStop(1, "#F1EBDD");
      ctx.fillStyle = grad;
      ctx.fillRect(x, y, A.cellW, A.cellH);
      paintSymbol(ctx, symbol, x + A.cellW / 2, y + A.cellH / 2);
      ctx.fillStyle = "#D8CFBD";
      ctx.fillRect(x, y + A.cellH - 1, A.cellW, 1);
    });
    ctx.fillStyle = "#B8AE98";
    ctx.fillRect(r * A.cellW + A.cellW - 1, 0, 1, A.height);
  });
}

function paintSymbol(ctx: Ctx2D, s: SlotSymbol, cx: number, cy: number): void {
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  switch (s) {
    case "seven":
      ctx.font = `900 56px ${FONT}`;
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#5E0D12";
      ctx.strokeText("7", cx, cy + 2);
      ctx.fillStyle = "#E3262E";
      ctx.fillText("7", cx, cy + 2);
      break;
    case "bar":
      roundRect(ctx, cx - 32, cy - 15, 64, 30, 6);
      ctx.fillStyle = "#1D1F26";
      ctx.fill();
      ctx.fillStyle = "#FFFFFF";
      ctx.font = `900 21px ${FONT}`;
      ctx.fillText("BAR", cx, cy + 1);
      break;
    case "cherry": {
      ctx.strokeStyle = "#2F7D32";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(cx - 10, cy + 6);
      ctx.quadraticCurveTo(cx - 6, cy - 14, cx + 6, cy - 20);
      ctx.moveTo(cx + 11, cy + 8);
      ctx.quadraticCurveTo(cx + 9, cy - 10, cx + 6, cy - 20);
      ctx.stroke();
      ctx.fillStyle = "#4CAF50";
      ctx.beginPath();
      ctx.ellipse(cx + 13, cy - 19, 9, 4, -0.4, 0, Math.PI * 2);
      ctx.fill();
      for (const [dx, dy] of [
        [-10, 12],
        [11, 14],
      ] as const) {
        ctx.beginPath();
        ctx.arc(cx + dx, cy + dy, 11, 0, Math.PI * 2);
        ctx.fillStyle = "#D7192A";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx + dx - 4, cy + dy - 4, 3, 0, Math.PI * 2);
        ctx.fillStyle = "#FF8A8A";
        ctx.fill();
      }
      break;
    }
    case "bell": {
      ctx.fillStyle = "#E9B21B";
      ctx.strokeStyle = "#8A6200";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx - 20, cy + 14);
      ctx.quadraticCurveTo(cx - 16, cy + 8, cx - 14, cy - 4);
      ctx.quadraticCurveTo(cx - 12, cy - 22, cx, cy - 22);
      ctx.quadraticCurveTo(cx + 12, cy - 22, cx + 14, cy - 4);
      ctx.quadraticCurveTo(cx + 16, cy + 8, cx + 20, cy + 14);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy + 17, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "blank":
      ctx.fillStyle = "#E4DCCB";
      ctx.fillRect(cx - 14, cy - 1, 28, 2);
      break;
  }
}

/** UVs of a reel window showing `rows` cells centred on fractional stop `pos` of reel `reel` (wraps by repeat). */
export function reelWindowUv(reel: number, pos: number, rows = 3): UvRect {
  const A = REEL_ATLAS;
  const u0 = (reel * A.cellW + 1) / A.width;
  const u1 = ((reel + 1) * A.cellW - 2) / A.width;
  // Cell i spans canvas rows [i, i + 1) * cellH; its centre is at i + 0.5.
  const centre = (pos + 0.5) / SLOT_STOPS;
  const half = rows / 2 / SLOT_STOPS;
  const stripH = (SLOT_STOPS * A.cellH) / A.height;
  return { u0, u1, v1: 1 - (centre - half) * stripH, v0: 1 - (centre + half) * stripH };
}

// ---------------------------------------------------------------------------------------------------------------------
// Money wheel face (768 x 768): 54 segments clockwise from the top
// ---------------------------------------------------------------------------------------------------------------------

export const WHEEL_COLORS: Record<MoneyWheelSymbol, { fill: string; ink: string }> = {
  "1": { fill: "#F2C84B", ink: "#3A2A00" },
  "2": { fill: "#3E7FD9", ink: "#FFFFFF" },
  "5": { fill: "#8B4FC6", ink: "#FFFFFF" },
  "10": { fill: "#2E9E62", ink: "#FFFFFF" },
  "20": { fill: "#EC7A2C", ink: "#FFFFFF" },
  star: { fill: "#D3323A", ink: "#FFE27A" },
};

export const WHEEL_TEX = 768;

function star(ctx: Ctx2D, cx: number, cy: number, r: number, rot = 0): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = rot - Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 === 0 ? r : r * 0.45;
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export function paintWheel(ctx: Ctx2D): void {
  const S = WHEEL_TEX;
  const c = S / 2;
  const R = S / 2 - 2;
  const n = MONEY_WHEEL_SEGMENTS.length;
  const step = (Math.PI * 2) / n;
  ctx.clearRect(0, 0, S, S);
  ctx.fillStyle = "#3A2A12";
  ctx.fillRect(0, 0, S, S);
  MONEY_WHEEL_SEGMENTS.forEach((sym, i) => {
    // Canvas angles run clockwise (y down): segment i is centred at the top plus i steps clockwise.
    const mid = -Math.PI / 2 + i * step;
    ctx.beginPath();
    ctx.moveTo(c, c);
    ctx.arc(c, c, R, mid - step / 2, mid + step / 2);
    ctx.closePath();
    ctx.fillStyle = WHEEL_COLORS[sym].fill;
    ctx.fill();
    ctx.strokeStyle = "#F7E7B0";
    ctx.lineWidth = 2;
    ctx.stroke();
    // Label along the radius, reading outward (the way it passes under the pointer).
    ctx.save();
    ctx.translate(c, c);
    ctx.rotate(mid);
    ctx.fillStyle = WHEEL_COLORS[sym].ink;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (sym === "star") {
      star(ctx, R * 0.8, 0, 17, Math.PI / 2);
      ctx.fill();
    } else {
      ctx.font = `900 ${sym.length > 1 ? 30 : 34}px ${FONT}`;
      ctx.fillText(sym, R * 0.8, 1);
    }
    ctx.restore();
  });
  // Inner rings and hub.
  ctx.beginPath();
  ctx.arc(c, c, R * 0.58, 0, Math.PI * 2);
  ctx.fillStyle = "#1E2A4A";
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#E8C45A";
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(c, c, R * 0.995, 0, Math.PI * 2);
  ctx.lineWidth = 5;
  ctx.strokeStyle = "#E8C45A";
  ctx.stroke();
  for (let k = 0; k < 12; k++) {
    star(ctx, c + Math.cos((k * Math.PI) / 6) * R * 0.46, c + Math.sin((k * Math.PI) / 6) * R * 0.46, 9);
    ctx.fillStyle = "#F2D06B";
    ctx.fill();
  }
  ctx.fillStyle = "#F7E7B0";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `800 40px ${FONT}`;
  ctx.fillText("TOWNSY", c, c - 22);
  ctx.font = `700 26px ${FONT}`;
  ctx.fillText("MONEY WHEEL", c, c + 20);
}

// ---------------------------------------------------------------------------------------------------------------------
// Blackjack felt for a half disc (1024 x 512: the dealer edge along the top, the arc touching the bottom)
// ---------------------------------------------------------------------------------------------------------------------

export const FELT_TEX = { width: 1024, height: 512 } as const;

function arcText(ctx: Ctx2D, text: string, cx: number, cy: number, r: number, size: number, spacing: number): void {
  ctx.font = `800 ${size}px ${FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const widths = [...text].map((ch) => ctx.measureText(ch).width + spacing);
  const total = widths.reduce((a, b) => a + b, 0);
  // Angle from straight down (canvas +y), positive toward canvas +x; characters left (west) to right (east).
  let a = -total / r / 2;
  [...text].forEach((ch, i) => {
    const w = widths[i]!;
    const mid = a + w / r / 2;
    ctx.save();
    ctx.translate(cx + Math.sin(mid) * r, cy + Math.cos(mid) * r);
    ctx.rotate(-mid);
    ctx.fillText(ch, 0, 0);
    ctx.restore();
    a += w / r;
  });
}

/** `seatDirs` are the seats' unit directions from the pivot (x east, z north; seats are south: z < 0). */
export function paintFelt(ctx: Ctx2D, seatDirs: ReadonlyArray<{ x: number; z: number }>, chipRadius: number): void {
  const { width: W, height: H } = FELT_TEX;
  const cx = W / 2;
  const R = H;
  const g = ctx.createRadialGradient(cx, 0, 40, cx, 0, R);
  g.addColorStop(0, "#23805A");
  g.addColorStop(1, "#17603F");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // Felt grain.
  for (let i = 0; i < 2600; i++) {
    const x = (i * 7919) % W;
    const y = (i * 104729) % H;
    ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.035)" : "rgba(0,0,0,0.05)";
    ctx.fillRect(x, y, 2, 2);
  }
  // Between the dealer's cards (up to ~0.4 R) and the players' cards (from ~0.6 R).
  ctx.fillStyle = "#F2D06B";
  arcText(ctx, "BLACKJACK PAYS 3 TO 2", cx, 0, R * 0.535, 30, 3);
  ctx.fillStyle = "rgba(247,231,176,0.85)";
  arcText(ctx, "DEALER STANDS ON ALL 17s", cx, 0, R * 0.44, 19, 2);
  ctx.strokeStyle = "rgba(242,208,107,0.7)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, 0, R * 0.485, 0, Math.PI);
  ctx.stroke();
  // Betting circles where each seat's chips go.
  for (const d of seatDirs) {
    const x = cx + d.x * R * chipRadius;
    const y = -d.z * R * chipRadius;
    ctx.beginPath();
    ctx.arc(x, y, 30, 0, Math.PI * 2);
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(247,231,176,0.8)";
    ctx.stroke();
  }
  // Rim line just inside the edge.
  ctx.beginPath();
  ctx.arc(cx, 0, R - 8, 0, Math.PI);
  ctx.lineWidth = 4;
  ctx.strokeStyle = "rgba(0,0,0,0.25)";
  ctx.stroke();
}

// ---------------------------------------------------------------------------------------------------------------------
// Floors

/** The medallion rug in the middle of the room. */
export function paintMedallion(ctx: Ctx2D, w: number, h: number): void {
  ctx.fillStyle = "#6E1E2A";
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = "#D9B25A";
  ctx.lineWidth = 10;
  ctx.strokeRect(14, 14, w - 28, h - 28);
  ctx.lineWidth = 3;
  ctx.strokeRect(30, 30, w - 60, h - 60);
  ctx.beginPath();
  ctx.ellipse(w / 2, h / 2, w * 0.36, h * 0.34, 0, 0, Math.PI * 2);
  ctx.fillStyle = "#3B2440";
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.stroke();
  for (let k = 0; k < 16; k++) {
    const a = (k * Math.PI) / 8;
    star(ctx, w / 2 + Math.cos(a) * w * 0.36, h / 2 + Math.sin(a) * h * 0.34, 9, a);
    ctx.fillStyle = "#F2D06B";
    ctx.fill();
  }
  ctx.fillStyle = "#F2D06B";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 ${Math.round(h * 0.15)}px ${FONT}`;
  ctx.fillText("TOWNSY", w / 2, h * 0.43);
  ctx.font = `800 ${Math.round(h * 0.09)}px ${FONT}`;
  ctx.fillText("CASINO", w / 2, h * 0.6);
  for (const [x, y] of [
    [44, 44],
    [w - 44, 44],
    [44, h - 44],
    [w - 44, h - 44],
  ] as const) {
    star(ctx, x, y, 14);
    ctx.fill();
  }
}
// ---------------------------------------------------------------------------------------------------------------------

export function paintCarpet(ctx: Ctx2D, base: string, line: string, dot: string): void {
  const S = 256;
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, S, S);
  for (let i = 0; i < 1800; i++) {
    const x = (i * 7919) % S;
    const y = (i * 6271) % S;
    ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)";
    ctx.fillRect(x, y, 2, 2);
  }
  ctx.strokeStyle = line;
  ctx.lineWidth = 5;
  // A diamond lattice (tiles seamlessly).
  ctx.beginPath();
  ctx.moveTo(0, S / 2);
  ctx.lineTo(S / 2, 0);
  ctx.lineTo(S, S / 2);
  ctx.lineTo(S / 2, S);
  ctx.closePath();
  ctx.stroke();
  ctx.fillStyle = dot;
  for (const [x, y] of [
    [S / 2, S / 2],
    [0, 0],
    [S, 0],
    [0, S],
    [S, S],
  ] as const) {
    star(ctx, x, y, 18, 0);
    ctx.fill();
  }
  for (const [x, y] of [
    [S / 4, S / 4],
    [(3 * S) / 4, S / 4],
    [S / 4, (3 * S) / 4],
    [(3 * S) / 4, (3 * S) / 4],
  ] as const) {
    ctx.beginPath();
    ctx.arc(x, y, 6, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function paintPlanks(ctx: Ctx2D): void {
  const S = 256;
  const rows = 8;
  for (let r = 0; r < rows; r++) {
    const y = (r * S) / rows;
    const tone = ["#8A5A36", "#7E5131", "#93613B", "#835534"][r % 4]!;
    ctx.fillStyle = tone;
    ctx.fillRect(0, y, S, S / rows);
    ctx.fillStyle = "rgba(0,0,0,0.28)";
    ctx.fillRect(0, y, S, 2);
    const off = (r * 97) % S;
    ctx.fillRect(off, y, 2, S / rows);
    for (let k = 0; k < 6; k++) {
      ctx.fillStyle = "rgba(255,240,220,0.06)";
      ctx.fillRect(0, y + 5 + k * 4, S, 1);
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Signs
// ---------------------------------------------------------------------------------------------------------------------

export function paintBetLayout(ctx: Ctx2D, w: number, h: number): Array<{ symbol: MoneyWheelSymbol; x0: number; x1: number }> {
  ctx.fillStyle = "#16365C";
  ctx.fillRect(0, 0, w, h);
  const cells: Array<{ symbol: MoneyWheelSymbol; x0: number; x1: number }> = [];
  const n = MONEY_WHEEL_SYMBOLS.length;
  const pad = 8;
  const cw = (w - pad * (n + 1)) / n;
  MONEY_WHEEL_SYMBOLS.forEach((s, i) => {
    const x = pad + i * (cw + pad);
    roundRect(ctx, x, pad, cw, h - pad * 2, 10);
    ctx.fillStyle = WHEEL_COLORS[s].fill;
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#F7E7B0";
    ctx.stroke();
    ctx.fillStyle = WHEEL_COLORS[s].ink;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (s === "star") {
      star(ctx, x + cw / 2, h * 0.4, h * 0.22);
      ctx.fill();
    } else {
      ctx.font = `900 ${Math.round(h * 0.42)}px ${FONT}`;
      ctx.fillText(s, x + cw / 2, h * 0.42);
    }
    ctx.font = `700 ${Math.round(h * 0.16)}px ${FONT}`;
    ctx.fillText(`${MONEY_WHEEL_PAYS[s]} TO 1`, x + cw / 2, h * 0.78);
    cells.push({ symbol: s, x0: x / w, x1: (x + cw) / w });
  });
  return cells;
}

/** A glowing sign: rounded panel, a big title and an optional subtitle. */
export function paintSign(ctx: Ctx2D, w: number, h: number, opts: { title: string; sub?: string; bg: string; ink: string; edge: string; titleSize?: number; subSize?: number }): void {
  ctx.clearRect(0, 0, w, h);
  roundRect(ctx, 3, 3, w - 6, h - 6, Math.min(w, h) * 0.12);
  ctx.fillStyle = opts.bg;
  ctx.fill();
  ctx.lineWidth = Math.max(3, h * 0.04);
  ctx.strokeStyle = opts.edge;
  ctx.stroke();
  ctx.fillStyle = opts.ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const ts = opts.titleSize ?? h * (opts.sub ? 0.42 : 0.6);
  ctx.font = `900 ${Math.round(ts)}px ${FONT}`;
  ctx.fillText(opts.title, w / 2, opts.sub ? h * 0.38 : h * 0.52, w * 0.92);
  if (opts.sub) {
    ctx.font = `700 ${Math.round(opts.subSize ?? h * 0.2)}px ${FONT}`;
    ctx.fillText(opts.sub, w / 2, h * 0.75, w * 0.92);
  }
}

/** The play chips sign's lines under its title (ADR-0024; ADR-0048: playing needs an account, first and in green). */
export const CHIPS_SIGN_LINES: readonly string[] = [
  "Sign up (18+) to play.",
  "Just for fun: no value.",
  "Shared with Townsy Downs,",
  "kept for your next visit.",
  "Under 100? You get 100.",
  "Not coins, not credit.",
];

/** The play chips explainer by the entrance. */
export function paintChipsSign(ctx: Ctx2D, w: number, h: number): void {
  ctx.fillStyle = "#FBF8F2";
  ctx.fillRect(0, 0, w, h);
  ctx.lineWidth = 10;
  ctx.strokeStyle = "#1F5C4A";
  ctx.strokeRect(5, 5, w - 10, h - 10);
  ctx.fillStyle = "#1F5C4A";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 ${Math.round(h * 0.15)}px ${FONT}`;
  ctx.fillText("PLAY CHIPS", w / 2, h * 0.17);
  CHIPS_SIGN_LINES.forEach((l, i) => {
    ctx.fillStyle = i === 0 ? "#1F5C4A" : "#1B1F2A";
    ctx.font = `${i === 0 ? 900 : 700} ${Math.round(h * 0.08)}px ${FONT}`;
    ctx.fillText(l, w / 2, h * (0.33 + i * 0.11), w * 0.9);
  });
}

/** The slot machines' toppers: one row per machine (256 x 512, rows 256 x 64). */
export const TOPPER_NAMES: readonly string[] = SLOT_MACHINE_NAMES;
export const TOPPER_TEX = { width: 256, height: 512, rowH: 64 } as const;
export const SLOT_COLORS = ["#C8323C", "#2F5FD0", "#8B4FC6", "#2E9E62", "#EC7A2C", "#D33A7A", "#1E8C9E", "#B8892A"];

export function paintToppers(ctx: Ctx2D): void {
  const T = TOPPER_TEX;
  TOPPER_NAMES.forEach((name, i) => {
    const y = i * T.rowH;
    ctx.fillStyle = SLOT_COLORS[i]!;
    ctx.fillRect(0, y, T.width, T.rowH);
    ctx.fillStyle = "rgba(255,255,255,0.18)";
    ctx.fillRect(0, y, T.width, T.rowH * 0.45);
    ctx.fillStyle = "#FFF6D8";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `900 30px ${FONT}`;
    ctx.fillText(name, T.width / 2, y + T.rowH / 2 + 1, T.width - 12);
  });
}

/** The marquee above the slot row: the paytable in one line (the font shrinks until it fits). */
export function paintSlotMarquee(ctx: Ctx2D, w: number, h: number): void {
  ctx.fillStyle = "#1A1430";
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = "#F2D06B";
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, w - 6, h - 6);
  const parts = ["SLOTS PAY × BET", "7 7 7  ×50", "BAR BAR BAR  ×20", "BELL BELL BELL  ×10", "CHERRY ×3  ×5", "TWO CHERRIES  ×2", "ONE CHERRY  ×1"];
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  let size = Math.round(h * 0.5);
  let widths: number[] = [];
  for (; size > 10; size -= 2) {
    ctx.font = `800 ${size}px ${FONT}`;
    widths = parts.map((p) => ctx.measureText(p).width);
    if (widths.reduce((a, b) => a + b, 0) + size * 1.2 * (parts.length + 1) <= w) break;
  }
  const gap = (w - widths.reduce((a, b) => a + b, 0)) / (parts.length + 1);
  let x = gap;
  parts.forEach((p, i) => {
    ctx.fillStyle = i === 0 ? "#7FE0FF" : "#FFE27A";
    ctx.fillText(p, x + widths[i]! / 2, h / 2 + 2);
    x += widths[i]! + gap;
  });
}
